/**
 * Rapor taslağını düzenleme ve gönderme.
 *
 * Sistem metni yazar, uygulayıcı sorumluluğu alır. Bu yüzden gönderme
 * tek tıkla olmuyor: metin düzenlenebilir halde önünüze geliyor ve
 * "Gönder" ayrı bir adım.
 *
 * Gönderildikten sonra ekran salt okunur hale geliyor — veritabanı da
 * zaten içeriği donduruyor. Düzeltmek değil, yeni rapor üretmek
 * gerekiyor: veliye giden metnin sonradan değişmemesi ürünün bir
 * vaadi.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  HEDEF_ADI,
  YAKINLIK_ADI,
  api,
  type RaporDosyasi,
  type RaporIcerik,
} from '../api'

function gun(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('tr-TR', { dateStyle: 'medium' })
}

export function RaporDuzenle({
  raporId,
  onGeri,
}: {
  raporId: string
  onGeri: () => void
}) {
  const [dosya, setDosya] = useState<RaporDosyasi | null>(null)
  const [icerik, setIcerik] = useState<RaporIcerik | null>(null)
  const [kisiId, setKisiId] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bilgi, setBilgi] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)
  const [gonderOnayi, setGonderOnayi] = useState(false)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const d = await api.rapor(raporId)
      setDosya(d)
      setIcerik(d.rapor.icerik)
      setKisiId(
        d.rapor.gonderilenKisiId ??
          d.ilgiliKisiler.find((k) => k.portal)?.id ??
          d.ilgiliKisiler[0]?.id ??
          '',
      )
    } catch {
      setHata('Rapor açılamadı.')
    }
  }, [raporId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  if (hata && !dosya) {
    return (
      <>
        <div className="uyari">{hata}</div>
        <button onClick={onGeri}>← Geri</button>
      </>
    )
  }
  if (!dosya || !icerik) return <p className="alt">Yükleniyor…</p>

  const gonderilmis = Boolean(dosya.rapor.gonderildi)
  const veliRaporu = dosya.rapor.hedef === 'parent'
  const degisti = JSON.stringify(icerik) !== JSON.stringify(dosya.rapor.icerik)

  const kaydet = async () => {
    setHata(null)
    setBilgi(null)
    setBekliyor(true)
    try {
      await api.raporKaydet(raporId, icerik)
      await yukle()
      setBilgi('Taslak kaydedildi.')
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'rapor_gonderilmis'
          ? 'Bu rapor gönderilmiş; içeriği değiştirilemiyor. Yeni rapor üretin.'
          : 'Kaydedilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  const gonder = async () => {
    setHata(null)
    setBilgi(null)
    setBekliyor(true)
    try {
      if (degisti) await api.raporKaydet(raporId, icerik)
      await api.raporGonder(raporId, veliRaporu ? kisiId : undefined)
      await yukle()
      setBilgi(
        veliRaporu
          ? 'Rapor gönderildi. Veli portalından görebilecek.'
          : 'Rapor gönderilmiş olarak işaretlendi.',
      )
    } catch (e) {
      const kod = e instanceof ApiHatasi ? e.kod : ''
      setHata(
        kod === 'alici_secilmeli'
          ? 'Veli raporunu göndermek için bir alıcı seçmeniz gerekiyor.'
          : kod === 'rapor_gonderilmis'
            ? 'Bu rapor zaten gönderilmiş.'
            : 'Gönderilemedi.',
      )
    } finally {
      setBekliyor(false)
      setGonderOnayi(false)
    }
  }

  const bolumGuncelle = (anahtar: string, alan: 'baslik' | 'metin', deger: string) =>
    setIcerik({
      ...icerik,
      bolumler: icerik.bolumler.map((b) => (b.anahtar === anahtar ? { ...b, [alan]: deger } : b)),
    })

  const fark = dosya.rapor.duzeltme?.fark

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onGeri}>← Dosya</button>
        {gonderilmis && <button onClick={() => window.print()}>Yazdır</button>}
      </div>

      <h1>{HEDEF_ADI[dosya.rapor.hedef] ?? dosya.rapor.hedef} raporu</h1>
      <p className="alt">
        {gun(dosya.rapor.donemBas)} – {gun(dosya.rapor.donemBit)} · üretildi{' '}
        {gun(dosya.rapor.uretildi)}
        {gonderilmis ? ` · gönderildi ${gun(dosya.rapor.gonderildi)}` : ' · taslak'}
      </p>

      {hata && <div className="uyari">{hata}</div>}
      {bilgi && <div className="bilgi">{bilgi}</div>}

      {gonderilmis && (
        <div className="bilgi">
          Bu rapor gönderildi ve içeriği artık değiştirilemiyor. Düzeltme gerekiyorsa yeni bir
          rapor üretin.
        </div>
      )}

      {!gonderilmis && fark && (fark.ozet || fark.bolumler.length > 0) && (
        <div className="bilgi">
          Bu taslakta {fark.bolumler.length + (fark.ozet ? 1 : 0)} bölüm elle düzeltildi.
        </div>
      )}

      <div className="kart">
        <label>
          Özet
          <textarea
            rows={3}
            value={icerik.ozet}
            readOnly={gonderilmis}
            onChange={(e) => setIcerik({ ...icerik, ozet: e.target.value })}
          />
        </label>
      </div>

      {icerik.bolumler.map((b) => (
        <div className="kart" key={b.anahtar}>
          <label>
            Başlık
            <input
              value={b.baslik}
              readOnly={gonderilmis}
              onChange={(e) => bolumGuncelle(b.anahtar, 'baslik', e.target.value)}
            />
          </label>
          <label style={{ marginBottom: 0 }}>
            Metin
            <textarea
              rows={4}
              value={b.metin}
              readOnly={gonderilmis}
              onChange={(e) => bolumGuncelle(b.anahtar, 'metin', e.target.value)}
            />
          </label>
        </div>
      ))}

      <div className="kisit">{icerik.kisitlar}</div>

      {!gonderilmis && (
        <div className="kart" style={{ marginTop: 16 }}>
          {veliRaporu && (
            <label>
              Kime gönderilecek
              <select value={kisiId} onChange={(e) => setKisiId(e.target.value)}>
                <option value="">Seçiniz…</option>
                {dosya.ilgiliKisiler.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.adSoyad} ({YAKINLIK_ADI[k.yakinlik] ?? k.yakinlik})
                    {k.portal ? '' : ' — portal kapalı'}
                  </option>
                ))}
              </select>
            </label>
          )}
          {veliRaporu && dosya.ilgiliKisiler.length === 0 && (
            <div className="uyari">
              Bu dosyada kayıtlı ilgili kişi yok. Veli raporunu göndermek için önce dosyaya
              veli eklenmesi gerekiyor.
            </div>
          )}
          {veliRaporu && kisiId && !dosya.ilgiliKisiler.find((k) => k.id === kisiId)?.portal && (
            <div className="bilgi">
              Seçtiğiniz kişinin veli portalı erişimi kapalı. Rapor kaydedilir ama kişi portaldan
              göremez.
            </div>
          )}

          <div className="dugmeler">
            <button onClick={() => void kaydet()} disabled={bekliyor || !degisti}>
              {bekliyor ? 'Bekleyin…' : 'Taslağı kaydet'}
            </button>
            {gonderOnayi ? (
              <button className="birincil" onClick={() => void gonder()} disabled={bekliyor}>
                Evet, gönder — geri alınamaz
              </button>
            ) : (
              <button
                className="birincil"
                onClick={() => setGonderOnayi(true)}
                disabled={bekliyor || (veliRaporu && !kisiId)}
              >
                Gönder
              </button>
            )}
            {gonderOnayi && (
              <button onClick={() => setGonderOnayi(false)} disabled={bekliyor}>
                Vazgeç
              </button>
            )}
          </div>
          <p className="alt" style={{ margin: '10px 0 0' }}>
            Gönderdikten sonra metin kilitlenir. Veliye gittiği haliyle kayıtta kalması gerekiyor.
          </p>
        </div>
      )}
    </>
  )
}
